import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState } from '@ui-kit';
import { useReportPreview } from '../hooks/useReportPreview';
import type { ApiError } from '../types/reportBuilder';

interface ReportPreviewDrawerProps {
  reportId: number;
  reportName?: string;
  onClose: () => void;
}

/**
 * ReportPreviewDrawer — 미리보기 오른쪽 패널 (dialog 상태)
 * GET /api/eraf/report-builder/reports/{id}/preview
 * ESC 닫기 + focus trap
 */
export function ReportPreviewDrawer({ reportId, reportName, onClose }: ReportPreviewDrawerProps) {
  const { t } = useTranslation();
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const previewQuery = useReportPreview(reportId, 50, true);

  // ESC 닫기
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // 포커스 이동
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const preview = previewQuery.data;

  return (
    <>
      {/* 오버레이 */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 900,
          background: 'rgba(0,0,0,0.35)',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* 드로어 패널 */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('reportBuilder.preview.title')}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(45vw, 720px)',
          minWidth: 320,
          zIndex: 901,
          background: 'var(--color-surface-background)',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--spacing-4) var(--spacing-5)',
            borderBottom: '1px solid var(--color-surface-border)',
            background: 'var(--color-surface-muted)',
            flexShrink: 0,
          }}
        >
          <div>
            <h2
              style={{
                fontSize: 'var(--font-size-md)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              {t('reportBuilder.preview.title')}
              {reportName ? ` — ${reportName}` : ''}
            </h2>
            {preview && (
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  margin: 'var(--spacing-1) 0 0',
                }}
              >
                {t('reportBuilder.preview.subtitle', { rows: preview.totalRows })}
              </p>
            )}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t('reportBuilder.preview.close')}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 'var(--font-size-xl)',
              color: 'var(--color-text-secondary)',
              lineHeight: 1,
              padding: 'var(--spacing-1)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            ×
          </button>
        </div>

        {/* 바디 */}
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--spacing-4)' }}>
          {/* loading skeleton */}
          {previewQuery.isLoading && (
            <div aria-label={t('reportBuilder.preview.loadingAriaLabel')} aria-busy="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: 32,
                    background: 'var(--color-surface-elevated)',
                    borderRadius: 'var(--radius-sm)',
                    marginBottom: 'var(--spacing-2)',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
              ))}
            </div>
          )}

          {/* error */}
          {previewQuery.isError && (
            <ErrorState
              title={t('reportBuilder.preview.error.loadFailed')}
              message={
                (previewQuery.error as unknown as ApiError)?.message ??
                t('reportBuilder.error.network')
              }
              onRetry={() => void previewQuery.refetch()}
            />
          )}

          {/* empty */}
          {!previewQuery.isLoading && !previewQuery.isError && preview?.totalRows === 0 && (
            <EmptyState
              title={t('reportBuilder.preview.empty')}
            />
          )}

          {/* default — 데이터 표 */}
          {!previewQuery.isLoading && !previewQuery.isError && preview && preview.totalRows > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'var(--font-size-sm)',
                }}
                aria-label={t('reportBuilder.preview.title')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {preview.columns.map((col, ci) => (
                      <th
                        key={ci}
                        style={{
                          textAlign: col.align === 'RIGHT' ? 'right' : col.align === 'CENTER' ? 'center' : 'left',
                          padding: 'var(--spacing-2) var(--spacing-3)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {col.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => {
                        const isBarcodeCol = preview.barcodeColumns.includes(
                          preview.columns[ci]?.header ?? ''
                        );
                        return (
                          <td
                            key={ci}
                            style={{
                              padding: 'var(--spacing-2) var(--spacing-3)',
                              borderBottom: '1px solid var(--color-surface-border)',
                              textAlign:
                                preview.columns[ci]?.align === 'RIGHT'
                                  ? 'right'
                                  : preview.columns[ci]?.align === 'CENTER'
                                  ? 'center'
                                  : 'left',
                              fontSize: 'var(--font-size-sm)',
                              color: isBarcodeCol
                                ? 'var(--color-text-muted)'
                                : 'var(--color-text-primary)',
                              fontStyle: isBarcodeCol ? 'italic' : 'normal',
                            }}
                          >
                            {isBarcodeCol
                              ? t('reportBuilder.preview.barcodePlaceholder')
                              : cell}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.truncated && (
                <p
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    marginTop: 'var(--spacing-2)',
                    textAlign: 'center',
                  }}
                >
                  {t('reportBuilder.preview.subtitle', { rows: preview.totalRows })}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </>
  );
}
